import React from "react";
import {
  Button,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
  Field,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { ChevronDownIcon } from "@fluentui/react-icons-mdl2";
import moment from "moment";
import { ReportParameter } from "../../../types/reportTypes";

interface FormFieldProps {
  param: ReportParameter;
  value: any;
  error: string;
  loading: boolean;
  dropdownOptions: any[];
  onInputChange: (paramLabel: string, value: any) => void;
  onBlur: (paramLabel: string) => void;
  onOpenPopup: (paramLabel: string) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  param,
  value,
  error,
  loading,
  dropdownOptions,
  onInputChange,
  onBlur,
  onOpenPopup,
}) => {
  const onFormatDate = (date?: Date): string =>
    date ? moment(date).format("DD-MM-YYYY") : "";

  const renderSelectField = () => {
    if (param.popup) {
      return (
        <>
          <Button onClick={() => onOpenPopup(param.label)}>
            {(() => {
              if (!value) return param.placeholder;

              const attributes = param.apiEndpoint?.attributes || [];

              const getName = (item: any): string => {
                return attributes
                  .map((attr: any) => item[attr.value])
                  .filter(Boolean)
                  .join(" ");
              };

              if (param.multiSelect && Array.isArray(value)) {
                if (value.length === 0) return param.placeholder;

                const names = value.map(getName);

                if (names.length <= 2) {
                  return names.join(", ");
                } else {
                  return `${names[0]}, ${names[1]}, + ${names.length - 2}`;
                }
              } else if (typeof value === "object" && value !== null) {
                const name = getName(value);
                return name || param.placeholder;
              } else if (typeof value === "string") {
                return value;
              }

              return param.placeholder;
            })()}
          </Button>
        </>
      );
    } else {
      return (
        <Menu onOpenChange={(_, { open }) => !open && onBlur(param.label)}>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="outline"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minWidth: "300px",
                maxWidth: "fit-content",
              }}
            >
              {(() => {
                if (loading) return "Loading...";
                if (!dropdownOptions?.length) return "No options available";

                const attributes = param.apiEndpoint?.attributes || [];
                const getName = (item: any): string => {
                  if (attributes.length === 0) {
                    // If no attributes, use the label property
                    return item.label || "";
                  }
                  return attributes
                    .map((attr: any) => item[attr.value])
                    .filter(Boolean)
                    .join(" ");
                };

                if (param.multiSelect && Array.isArray(value)) {
                  if (value.length === 0)
                    return param.placeholder || "Select options";

                  const names = value.map(getName).filter(Boolean);
                  if (names.length <= 2) {
                    return names.join(", ");
                  } else {
                    return `${names[0]}, ${names[1]}, + ${names.length - 2}`;
                  }
                } else if (typeof value === "object") {
                  return value.label || param.placeholder || "Select an option";
                } else if (typeof value === "string") {
                  return value;
                }

                return param.placeholder || "Select an option";
              })()}
              <ChevronDownIcon style={{ marginLeft: "8px" }} />
            </Button>
          </MenuTrigger>
          <MenuPopover style={{ width: 400, maxHeight: "50vh" }}>
            <MenuList>
              {dropdownOptions?.length ? (
                dropdownOptions.map((option, index) => (
                  <MenuItem
                    key={option.value || index}
                    onClick={() => {
                      if (param.multiSelect) {
                        const currentValue = value || [];
                        const isSelected = Array.isArray(currentValue)
                          ? currentValue.some(
                              (item) => item.value === option.value
                            )
                          : false;

                        if (isSelected) {
                          const newValue = Array.isArray(currentValue)
                            ? currentValue.filter(
                                (item) => item.value !== option.value
                              )
                            : [];
                          onInputChange(param.label, newValue);
                        } else {
                          const newValue = Array.isArray(currentValue)
                            ? [...currentValue, option]
                            : [option];
                          onInputChange(param.label, newValue);
                        }
                      } else {
                        onInputChange(param.label, option);
                      }
                    }}
                  >
                    {param.multiSelect && (
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(value)
                            ? value.some(
                                (item: any) => item.value === option.value
                              )
                            : false
                        }
                        readOnly
                        style={{ marginRight: "8px" }}
                      />
                    )}
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No options available</MenuItem>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }
  };

  const renderField = () => {
    switch (param.category) {
      case "select":
        return renderSelectField();

      case "datepicker":
        return (
          <DatePicker
            value={value ? new Date(value) : undefined}
            onSelectDate={(date) =>
              onInputChange(
                param.label,
                date
                  ? moment(date).format(param.validate?.format || "YYYY-MM-DD")
                  : ""
              )
            }
            placeholder={param.placeholder || "Select a date"}
            onBlur={() => onBlur(param.label)}
            formatDate={onFormatDate}
          />
        );

      case "timepicker":
        return (
          <Input
            type="time"
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
            placeholder="HH:mm"
          />
        );

      case "textarea":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      case "text":
        return (
          <Input
            value={value || ""}
            onChange={(e) => onInputChange(param.label, e.target.value)}
            onBlur={() => onBlur(param.label)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Field label={param?.label || ""} validationMessage={error}>
      {renderField()}
    </Field>
  );
};

export default FormField;
